// Overtime — 3-on-3 sudden-death past regulation.
//
// Owns:   the transition from regulation Play (period 3, tied) into a
//         3-on-3 sudden-death OT, the OT play-clock, and the OT-end
//         transitions (goal scored → GameOver; timer expired tied →
//         Shootout, deferred to Slice 9 which currently falls back to
//         a Draw).
// Reads:  state.score, state.clock, state.players, tuning.clock.
// Mutates: state.clock (phase, remainingTicks, nextPlayPhase),
//          state.puck, state.players (bench LW/RW, reset C and D to
//          faceoff layout via resetPlayersToSpawn).
// Emits:  PeriodEnded(periodIndex=2) (the regulation ender, emitted by
//         the caller in `core/SimTick.ts`), then the Faceoff event
//         from the OT-start faceoff layout. No bespoke "OvertimeStarted"
//         event yet — the renderer reads phase === "Overtime" directly.
//
// Bench mechanic:
//   - Real NHL OT runs 3-on-3: each team has C + 2 D on the ice, the
//     two W are on the bench. Our roster is fixed (no line changes),
//     so we simulate this by marking LW and RW as `benched`.
//   - Benched players are parked just outside the rink (off the board
//     edge) and skipped in every per-tick loop that would otherwise
//     skate / possess / collide them.
//   - Returning to regulation play (e.g. after Slice 9's shootout
//     finishes — not implemented yet) would call `unbenchAll`. For
//     Slice 8 the bench persists until GameOver.
//
// Zero-allocation.

import { resetPlayersToSpawn } from "../state/initial.js";
import { tuning } from "../state/tuning.js";
import type { MatchClockPhase, MatchState, PlayerState } from "../state/types.js";

// Where benched players sit. Parked one meter outside the +y board so
// the rink-collision pass (which we skip for benched players) won't
// drag them back. Stacked along +x by id so they don't overlap.
function benchPositionFor(player: PlayerState): { x: number; y: number } {
  const halfW = tuning.rink.width / 2;
  // Both teams share the +y bench in our simplified arena. Spread by
  // role so home / away don't overlap visually.
  const y = halfW + 1.5;
  // home-LW = -8, home-RW = -4, away-LW = +4, away-RW = +8
  const teamSign = player.teamId === "home" ? -1 : 1;
  const roleOffset = player.position === "LW" ? 4 : 8;
  return { x: teamSign * roleOffset, y };
}

/**
 * Player-role / phase test: is this skater currently on the bench?
 * Centralised so callers can use one predicate instead of duplicating
 * the role + phase logic. Benched status follows the player's `benched`
 * flag, not the phase — the flag is the source of truth.
 */
export function isBenched(player: PlayerState): boolean {
  return player.benched;
}

/**
 * Should this clock phase allow normal "live play" mechanics (skating,
 * possession, puck integration, etc.)? Regulation Play, OT, and the
 * Shoot sub-phase of Shootout all qualify. Shootout Setup / Result
 * sub-phases freeze the puck via the shootout machine itself, so
 * letting `isLive` stay true here is harmless.
 */
export function isLivePlayPhase(phase: MatchClockPhase): boolean {
  return phase === "Play" || phase === "Overtime" || phase === "Shootout";
}

/**
 * Transition from regulation Play (period 3 ended tied) into a 3-on-3
 * sudden-death OT faceoff at centre. Wings are sent to the bench; C
 * and D reset to their faceoff spawn; the OT clock loads its
 * `overtimeTicks` budget that starts ticking once Faceoff resolves
 * into the Overtime play phase.
 *
 * Called from `core/SimTick.ts`'s clock branch when period 3 ends with
 * a tied score.
 */
export function enterOvertime(state: MatchState): void {
  // Bench the wings.
  for (const p of state.players) {
    if (p.position === "LW" || p.position === "RW") {
      p.benched = true;
      const bench = benchPositionFor(p);
      p.pos.x = bench.x;
      p.pos.y = bench.y;
      p.vel.x = 0;
      p.vel.y = 0;
      p.moveDir.x = 0;
      p.moveDir.y = 0;
      p.hasPuck = false;
      p.possessionQuality = 0;
      p.action = "Idle";
      p.actionTimer = 0;
      p.stunTicks = 0;
      p.recoveryTicks = 0;
      p.shootHoldTicks = 0;
      p.passHoldTicks = 0;
      p.receivedPassFromTick = -1;
      p.isProtecting = false;
    }
  }
  // Reset the remaining skaters (C + D) to their regulation faceoff
  // spawn at centre. `resetPlayersToSpawn` is benched-aware (skips
  // benched players) so it doesn't undo the bench above.
  resetPlayersToSpawn(state.players);

  // Reset the puck to centre for the OT faceoff drop.
  state.puck.pos.x = 0;
  state.puck.pos.y = 0;
  state.puck.vel.x = 0;
  state.puck.vel.y = 0;
  state.puck.mode = "Loose";
  state.puck.ownerId = null;
  state.puck.lastTouchedBy = null;
  state.puck.noRecaptureUntilTick = state.tick + tuning.clock.faceoffPauseTicks;
  state.puck.opponentLockoutPlayerId = null;
  state.puck.opponentLockoutUntilTick = 0;

  // Run the faceoff pause first; when it resolves the post-faceoff
  // branch will read `nextPlayPhase` and start the OT play timer.
  state.clock.phase = "Faceoff";
  state.clock.remainingTicks = tuning.clock.faceoffPauseTicks;
  state.clock.nextPlayPhase = "Overtime";
  state.events.push({ type: "Faceoff", tick: state.tick, spot: "Center" });
}

/**
 * Restore wings to the active lineup. Called after the shootout
 * finishes (Slice 9) — currently unused by Slice 8 because the game
 * ends as a draw if OT expires.
 */
export function unbenchAll(state: MatchState): void {
  for (const p of state.players) p.benched = false;
}
